import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@stores/authStore'
import { apiClient } from '@services/api'
import { AuthLayout } from '@components/AuthLayout'

export function LoginPage({ admin = false }: { admin?: boolean }) {
  const navigate = useNavigate()
  const { setAuth, setError, setLoading, error, isLoading } = useAuthStore()
  const [formData, setFormData] = useState({ email: '', password: '' })
  const [showPassword, setShowPassword] = useState(false)
  const [selfSignup, setSelfSignup] = useState(false)
  useEffect(() => { setError(null); apiClient.get<any>('/auth/status').then((r) => setSelfSignup(!!r?.data?.selfSignup)).catch(() => {}) }, [admin])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    try {
      const data = await apiClient.login(formData.email.trim(), formData.password)
      if (admin && !data.user?.isPlatformAdmin) {
        setError('This sign-in is for Xora platform administrators. Company users sign in on the main page.')
        return
      }
      setAuth(data.accessToken, data.refreshToken, data.user)
      navigate(data.user?.isPlatformAdmin ? '/admin' : '/dashboard')
    } catch (err: any) {
      setError(err.response?.data?.message || 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthLayout>
      <div className="card w-full max-w-[400px] !p-8">
        {admin && (
          <div className="inline-flex items-center gap-2 mb-3 px-2 py-1 rounded-[6px] text-[11px] font-bold tracking-wide uppercase" style={{ background: 'var(--primary-soft)', color: 'var(--primary)' }}>
            Xora Platform Console
          </div>
        )}
        <h1 className="text-[20px] font-bold tracking-[-0.01em] m-0">{admin ? 'Administrator sign in' : 'Sign in'}</h1>
        <p className="text-[13px] text-ink-soft mt-1 mb-6">{admin ? 'For Xora India staff — onboard and manage companies on the platform.' : 'Use your company account to continue.'}</p>

        <form onSubmit={handleSubmit} noValidate>
          {error && <div className="alert alert-error">{error}</div>}

          <label htmlFor="login-email">Email Address</label>
          <input
            id="login-email"
            type="email"
            required
            autoComplete="username"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="you@company.com"
            disabled={isLoading}
          />

          <label htmlFor="login-password" className="mt-[14px]">
            Password
          </label>
          <div className="relative">
            <input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              required
              autoComplete="current-password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              placeholder="••••••••"
              disabled={isLoading}
              className="!pr-16"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[11.5px] font-semibold text-primary-500 hover:underline"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </div>

          <button type="submit" disabled={isLoading} className="btn-primary w-full mt-5">
            {isLoading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>

        {admin ? (
          <div className="mt-5 text-[13px] text-ink-soft text-center">
            Company user? <Link to="/login" className="text-primary-500 font-semibold hover:underline">Sign in here</Link>
          </div>
        ) : (
          <>
            <div className="mt-5 text-[13px] text-ink-soft text-center">
              {selfSignup ? (
                <>
                  New company?{' '}
                  <Link to="/signup" className="text-primary-500 font-semibold hover:underline">
                    Create your account
                  </Link>
                </>
              ) : (
                <>New company? Contact <a href="mailto:support@xoraindia.com" className="text-primary-500 font-semibold hover:underline">support@xoraindia.com</a> — Xora sets up your workspace and owner login.</>
              )}
            </div>

            <div className="mt-5 text-[11.5px] text-ink-soft text-center">
              Xora administrator? <Link to="/admin/login" className="text-primary-500 hover:underline">Platform console →</Link>
            </div>
          </>
        )}
      </div>
    </AuthLayout>
  )
}
